import { useState, useCallback } from 'react';
import userService, { User, UserFormData } from '../services/userService';
import { toast } from 'react-hot-toast';

export const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Не грузим полный список пользователей при монтировании —
  // GET /users доступен только admin, а форма статей нужна и editor.
  const fetchUsers = useCallback(async () => {
    try {
      console.log('Начало загрузки пользователей');
      setLoading(true);
      setError(null);
      const data = await userService.getUsers();
      console.log('Получены пользователи:', data);
      setUsers(data);
    } catch (err) {
      console.error('Детальная ошибка при загрузке пользователей:', err);
      setError('Ошибка при загрузке пользователей');
    } finally {
      setLoading(false);
    }
  }, []);

  const createUser = useCallback(async (userData: UserFormData) => {
    try {
      setLoading(true);
      setError(null);
      const newUser = await userService.createUser(userData);
      setUsers(prev => [...prev, newUser]);
      toast.success('Пользователь успешно создан');
      return newUser;
    } catch (err) {
      console.error('Ошибка при создании пользователя:', err);
      setError('Ошибка при создании пользователя');
      toast.error('Ошибка при создании пользователя');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateUser = useCallback(async (id: string, userData: Partial<UserFormData>) => {
    try {
      setLoading(true);
      setError(null);
      const updatedUser = await userService.updateUser(id, userData);
      setUsers(prev => prev.map(user => user.id === id ? updatedUser : user));
      toast.success('Пользователь успешно обновлен');
      return updatedUser;
    } catch (err) {
      setError('Ошибка при обновлении пользователя');
      toast.error('Ошибка при обновлении пользователя');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteUser = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      await userService.deleteUser(id);
      setUsers(prev => prev.filter(user => user.id !== id));
      toast.success('Пользователь успешно удален');
    } catch (err) {
      setError('Ошибка при удалении пользователя');
      toast.error('Ошибка при удалении пользователя');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getUsersByRole = useCallback(async (role: User['role']) => {
    try {
      setLoading(true);
      setError(null);
      const data = await userService.getUsersByRole(role);
      return data;
    } catch (err) {
      setError('Ошибка при загрузке пользователей');
      console.error('Ошибка при загрузке пользователей:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    users,
    loading,
    error,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    getUsersByRole
  };
}; 